import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Upload, Lightning, ClipboardText, Truck, ShieldCheck, Warning } from "@phosphor-icons/react";
import DocumentUpload, { documentUrl } from "@/components/inventory/DocumentUpload";
import { NCRDialog, QARecordDialog, pendingCount } from "@/components/inventory/QualityRecords";
import { useAccess } from "@/lib/access";

const TABS = [
  { key: "prs", label: "Purchase Requisitions", icon: ClipboardText, testId: "tab-prs" },
  { key: "pos", label: "Purchase Orders", icon: Truck, testId: "tab-pos" },
  { key: "grns", label: "GRN / QA", icon: ShieldCheck, testId: "tab-grns" },
  { key: "ncrs", label: "NCR / CAPA", icon: Warning, testId: "tab-ncrs" },
  { key: "ocr", label: "Invoice OCR", icon: Lightning, testId: "tab-ocr" },
];

const STATUS_TONE = {
  draft: "bg-slate-100 text-slate-700",
  approved: "bg-sky-50 text-sky-700",
  submitted: "bg-amber-50 text-amber-700",
  converted: "bg-emerald-50 text-emerald-700",
  open: "bg-sky-50 text-sky-700",
  partial: "bg-amber-50 text-amber-700",
  fulfilled: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-rose-50 text-rose-700",
  pending: "bg-amber-50 text-amber-700",
  accepted: "bg-emerald-50 text-emerald-700",
  rejected: "bg-rose-50 text-rose-700",
  not_required: "bg-slate-100 text-slate-600",
  closed: "bg-emerald-50 text-emerald-700",
};

export default function Procurement() {
  const { can } = useAccess();
  // ?tab=grns etc. lets other pages (e.g. dashboard alert tiles) deep-link to a tab.
  const [searchParams] = useSearchParams();
  const requested = searchParams.get("tab");
  const [tab, setTab] = useState(TABS.some((t) => t.key === requested) ? requested : "prs");
  const [data, setData] = useState({ prs: [], pos: [], grns: [], ncrs: [] });
  const [loading, setLoading] = useState(false);
  const [qaRecordId, setQaRecordId] = useState(null);
  const [openNcr, setOpenNcr] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [prs, pos, grns, ncrs] = await Promise.all([
        api.get("/inventory/prs"),
        api.get("/inventory/pos"),
        api.get("/inventory/grns"),
        api.get("/inventory/ncrs"),
      ]);
      setData({ prs: prs.data, pos: pos.data, grns: grns.data, ncrs: ncrs.data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const autoFromLow = async () => {
    try {
      const { data: pr } = await api.post("/inventory/prs/from-low-stock");
      toast.success(`${pr.code} created with ${pr.lines.length} lines`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No low stock items");
    }
  };

  const approvePR = async (id) => {
    try {
      await api.post(`/inventory/prs/${id}/approve`);
      toast.success("Approved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-6 space-y-5" data-testid="procurement-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-600 font-semibold">
            Inventory · Procurement & QC
          </div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Procurement Desk</h1>
          <p className="text-sm text-slate-500 mt-1">
            From requisition to receipt to QC — one flow, full ISO 22000 traceability.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {can("requisition.create") && (
          <button
            onClick={autoFromLow}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
            data-testid="auto-pr-btn"
          >
            <Lightning size={14} weight="bold" /> Auto-PR from low stock
          </button>
          )}
        </div>
      </div>

      <div className="border-b border-slate-200 flex items-center gap-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition ${active ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-900"}`}
              data-testid={t.testId}
            >
              <Icon size={14} weight={active ? "fill" : "bold"} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "prs" && <PRTable rows={data.prs} onApprove={can("po.create") ? approvePR : null} loading={loading} />}
      {tab === "pos" && <POTable rows={data.pos} loading={loading} />}
      {tab === "grns" && <GRNTable rows={data.grns} onChanged={load} loading={loading} canInspect={can("qa.decide")} onView={setQaRecordId} />}
      {tab === "ncrs" && <NCRTable rows={data.ncrs} loading={loading} onOpen={setOpenNcr} />}
      {tab === "ocr" && <InvoiceOCR />}

      {qaRecordId && (
        <QARecordDialog
          grnId={qaRecordId}
          ncr={data.ncrs.find((n) => n.grn_id === qaRecordId)}
          onClose={() => setQaRecordId(null)}
          onOpenNcr={(n) => { setQaRecordId(null); setOpenNcr(n); }}
        />
      )}
      {openNcr && (
        <NCRDialog
          key={openNcr.id}
          ncr={openNcr}
          canEdit={can("ncr.edit")}
          onClose={() => setOpenNcr(null)}
          onChanged={load}
          onViewGrn={(id) => { setOpenNcr(null); setQaRecordId(id); }}
        />
      )}
    </div>
  );
}

function Empty({ children }) {
  return (
    <div className="bg-white border border-dashed border-slate-200 rounded-lg px-6 py-12 text-center text-sm text-slate-500">
      {children}
    </div>
  );
}

function StatusChip({ value }) {
  return (
    <span className={`text-[10px] uppercase tracking-[0.16em] font-semibold px-2 py-1 rounded ${STATUS_TONE[value] || "bg-slate-100 text-slate-600"}`}>
      {(value || "").replace("_", " ") || "—"}
    </span>
  );
}

function PRTable({ rows, onApprove, loading }) {
  if (!loading && rows.length === 0) return <Empty>No purchase requisitions yet. Use &quot;Auto-PR from low stock&quot; to seed one.</Empty>;
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>{["Code", "Title", "Dept", "Lines", "Status", "Created", ""].map((h) => <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-100" data-testid={`pr-row-${r.id}`}>
              <td className="px-4 py-3 text-xs font-mono text-slate-700">{r.code}</td>
              <td className="px-4 py-3 text-sm font-medium text-slate-900">{r.title}</td>
              <td className="px-4 py-3 text-xs text-slate-500">{r.department}</td>
              <td className="px-4 py-3 text-sm tabular-nums">{(r.lines || []).length}</td>
              <td className="px-4 py-3"><StatusChip value={r.status} /></td>
              <td className="px-4 py-3 text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</td>
              <td className="px-4 py-3 text-right">
                {r.status === "draft" && onApprove && (
                  <button onClick={() => onApprove(r.id)} className="text-xs font-medium text-emerald-700 hover:text-emerald-900" data-testid={`pr-approve-${r.id}`}>Approve →</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function POTable({ rows, loading }) {
  const manageLink = (
    <Link to="/inventory/purchase-orders" className="text-emerald-700 font-medium hover:underline">Purchase Orders →</Link>
  );
  if (!loading && rows.length === 0) return <Empty>No purchase orders yet. Create and receive them in {manageLink}</Empty>;
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="px-4 py-2 text-xs text-slate-500 bg-slate-50 border-b border-slate-200">
        Read-only view. Create, receive and cancel POs in {manageLink}
      </div>
      <table className="w-full">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>{["Code", "Vendor", "Lines", "Total", "Status", "ETA"].map((h) => <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-100" data-testid={`po-row-${r.id}`}>
              <td className="px-4 py-3 text-xs font-mono text-slate-700">{r.code}</td>
              <td className="px-4 py-3 text-sm font-medium text-slate-900">{r.vendor_name}</td>
              <td className="px-4 py-3 text-sm tabular-nums">{(r.lines || []).length}</td>
              <td className="px-4 py-3 text-sm tabular-nums">₹{Number(r.total).toLocaleString("en-IN")}</td>
              <td className="px-4 py-3"><StatusChip value={r.status} /></td>
              <td className="px-4 py-3 text-xs text-slate-500">{r.expected_delivery || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GRNTable({ rows, onChanged, loading, canInspect, onView }) {
  const [inspecting, setInspecting] = useState(null);
  if (!loading && rows.length === 0) return <Empty>No GRNs yet. Stock In entries and PO receipts appear here for QA.</Empty>;
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>{["Code", "Source", "Vendor", "Invoice", "Lines", "QA Status", "Received", ""].map((h) => <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-100" data-testid={`grn-row-${r.id}`}>
              <td className="px-4 py-3 text-xs font-mono text-slate-700">{r.code}</td>
              <td className="px-4 py-3 text-xs text-slate-500">{r.po_code || "Direct Stock In"}</td>
              <td className="px-4 py-3 text-sm font-medium text-slate-900">{r.vendor_name}</td>
              <td className="px-4 py-3 text-xs">
                {r.invoice_doc_id ? (
                  <a href={documentUrl(r.invoice_doc_id)} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline">
                    {r.invoice_no || "View"} ↗
                  </a>
                ) : (
                  <span className="text-slate-400">{r.invoice_no || "—"}</span>
                )}
              </td>
              <td className="px-4 py-3 text-sm tabular-nums">{(r.lines || []).length}</td>
              <td className="px-4 py-3"><StatusChip value={r.qc_status} /></td>
              <td className="px-4 py-3 text-xs text-slate-500">{r.received_at}</td>
              <td className="px-4 py-3 text-right whitespace-nowrap">
                {r.qc_status === "pending" && canInspect && (
                  <button onClick={() => setInspecting(r)} className="text-xs font-medium text-emerald-700 hover:text-emerald-900" data-testid={`grn-inspect-${r.id}`}>
                    Inspect →
                  </button>
                )}
                {["accepted", "partial", "rejected"].includes(r.qc_status) && (
                  <button onClick={() => onView(r.id)} className="text-xs font-medium text-slate-600 hover:text-slate-900" data-testid={`grn-view-${r.id}`}>
                    View QA →
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {inspecting && (
        <QADialog
          grn={inspecting}
          onClose={() => setInspecting(null)}
          onDone={() => { setInspecting(null); onChanged(); }}
        />
      )}
    </div>
  );
}

function QADialog({ grn, onClose, onDone }) {
  const held = (grn.stock_refs || []).filter((r) => r.qc_required);
  const [notes, setNotes] = useState("");
  const [accepted, setAccepted] = useState(() => Object.fromEntries(held.map((r) => [r.stock_id, String(r.qty)])));
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Decision follows the accepted quantities: full = accept, none = reject, anything between = partial.
  const qtyOf = (r) => Number(accepted[r.stock_id] || 0);
  const approveDecision = held.every((r) => qtyOf(r) + 0.0001 >= Number(r.qty))
    ? "accept"
    : held.every((r) => qtyOf(r) <= 0) ? "reject" : "partial";
  const needsReason = approveDecision !== "accept";

  const submit = async (decision) => {
    if (decision !== "accept" && !notes.trim()) {
      toast.error("Enter the reason for rejection");
      return;
    }
    if (held.some((r) => qtyOf(r) < 0 || qtyOf(r) > Number(r.qty) + 0.0001)) {
      toast.error("Accepted qty must be between 0 and the received qty");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        decision,
        inspector_notes: notes.trim(),
        photo_doc_ids: photos.map((p) => p.id),
      };
      if (decision === "partial") {
        body.lines = held.map((r) => ({ stock_id: r.stock_id, accepted_qty: Number(accepted[r.stock_id] || 0) }));
      }
      await api.post(`/inventory/grns/${grn.id}/qc`, body);
      toast.success(
        decision === "accept" ? "Accepted — stock is now available" : decision === "reject" ? "Rejected — stock blocked, NCR raised" : "Partially accepted — NCR raised for the rest",
      );
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center px-4 py-6 overflow-y-auto" data-testid="qa-dialog">
      <form onSubmit={(e) => { e.preventDefault(); submit(approveDecision); }} className="bg-white rounded-lg shadow-xl w-full max-w-xl text-left">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="font-heading text-lg font-bold text-slate-900">QA Inspection — {grn.code}</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {grn.vendor_name || "—"} · Invoice {grn.invoice_no || "—"}
            {grn.invoice_doc_id && (
              <>
                {" · "}
                <a href={documentUrl(grn.invoice_doc_id)} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline">view invoice ↗</a>
              </>
            )}
          </p>
        </div>
        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
            {held.map((r) => (
              <div key={r.stock_id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate">{r.item_name || "Item"}</div>
                  <div className="text-xs text-slate-500">Batch {r.batch_no || "—"} · received {r.qty} {r.uom || ""}</div>
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  Accepted qty
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    max={r.qty}
                    value={accepted[r.stock_id]}
                    onChange={(e) => setAccepted((a) => ({ ...a, [r.stock_id]: e.target.value }))}
                    className="w-24 h-8 px-2 rounded-md border border-slate-200 text-sm"
                    data-testid={`qa-accepted-${r.stock_id}`}
                  />
                  <span className="w-6">{r.uom || ""}</span>
                </label>
              </div>
            ))}
          </div>
          {approveDecision === "partial" && (
            <p className="text-xs text-amber-700 -mt-2">Reduced qty — the balance will be blocked and an NCR raised.</p>
          )}

          <label className="block">
            <span className="block text-xs font-medium text-slate-600 mb-1">
              {needsReason ? "Reason for rejection *" : "Inspector notes"}
            </span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder={needsReason ? "e.g. Packaging damaged, moisture above spec" : "e.g. Within spec"}
              className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm"
              data-testid="qa-notes"
            />
          </label>

          <div>
            <span className="block text-xs font-medium text-slate-600 mb-1">Photos</span>
            <div className="space-y-2">
              {photos.map((p) => (
                <DocumentUpload key={p.id} kind="qa_photo" value={p} onChange={() => setPhotos((ps) => ps.filter((x) => x.id !== p.id))} />
              ))}
              <DocumentUpload kind="qa_photo" value={null} onChange={(doc) => doc && setPhotos((ps) => [...ps, doc])} label="Add photo" testId="qa-photo" />
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex items-center gap-2">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-md border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => submit("reject")}
            disabled={submitting}
            className="h-10 px-4 rounded-md border border-rose-300 bg-white text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
            data-testid="qa-reject"
          >
            Reject lot
          </button>
          <button
            type="submit"
            disabled={submitting || approveDecision === "reject"}
            className="h-10 px-5 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
            data-testid="qa-submit"
          >
            {submitting ? "Saving…" : approveDecision === "partial" ? "Approve partial" : "Approve & release"}
          </button>
        </div>
      </form>
    </div>
  );
}

function NCRTable({ rows, loading, onOpen }) {
  if (!loading && rows.length === 0) return <Empty>No NCRs raised. NCRs are auto-created when a GRN fails QC.</Empty>;
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>{["Code", "Title", "Vendor", "Blocked stock", "CAPA", "Status", "Raised", ""].map((h) => <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const pending = pendingCount(r);
            return (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`ncr-row-${r.id}`}>
                <td className="px-4 py-3 text-xs font-mono text-slate-700">{r.code}</td>
                <td className="px-4 py-3 text-sm font-medium text-slate-900">{r.title}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{r.vendor_name}</td>
                <td className="px-4 py-3 text-xs">
                  {pending > 0
                    ? <span className="text-amber-700 font-medium">{pending} awaiting decision</span>
                    : <span className="text-slate-500">All decided</span>}
                </td>
                <td className="px-4 py-3 text-sm tabular-nums">{(r.capa_actions || []).length}</td>
                <td className="px-4 py-3"><StatusChip value={r.status} /></td>
                <td className="px-4 py-3 text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => onOpen(r)} className="text-xs font-medium text-emerald-700 hover:text-emerald-900" data-testid={`ncr-open-${r.id}`}>
                    {r.status === "open" && pending > 0 ? "Decide →" : "View →"}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function InvoiceOCR() {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [model, setModel] = useState("");

  const onPick = async (file) => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/inventory/invoices/ocr", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data.parsed);
      setModel(data.model);
      toast.success("Invoice parsed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OCR failed");
    } finally {
      setBusy(false);
    }
  };

  const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

  return (
    <div className="space-y-5" data-testid="ocr-tab">
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-md bg-sky-50 text-sky-600 flex items-center justify-center shrink-0">
            <Lightning size={22} weight="duotone" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-lg font-bold text-slate-900">Vendor Invoice OCR</h3>
            <p className="text-sm text-slate-500 mt-1">
              Drop a PDF / PNG / JPG and we&apos;ll extract vendor, invoice no, date, GSTIN, line items and totals using Gemini 3 Pro. Use this to bootstrap GRN data without typing.
            </p>
            <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => onPick(e.target.files?.[0])} data-testid="ocr-file-input" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="mt-4 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
              data-testid="ocr-pick-btn"
            >
              <Upload size={14} weight="bold" /> {busy ? "Reading invoice…" : "Upload invoice"}
            </button>
          </div>
        </div>
      </div>

      {result && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="ocr-result">
          <div className="px-5 py-3 bg-emerald-50 border-b border-emerald-200 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-700 font-bold">Extracted invoice</div>
            <div className="text-[11px] text-emerald-700">model: {model}</div>
          </div>
          <div className="px-5 py-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Vendor" value={result.vendor_name} />
            <Field label="Invoice No" value={result.invoice_number} />
            <Field label="Date" value={result.invoice_date} />
            <Field label="GSTIN" value={result.gstin} />
          </div>
          <table className="w-full">
            <thead className="bg-slate-50 border-y border-slate-200">
              <tr>{["#", "Description", "HSN", "Qty", "UoM", "Unit ₹", "Line ₹"].map((h) => <th key={h} className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody>
              {(result.line_items || []).map((li, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-xs text-slate-500">{i + 1}</td>
                  <td className="px-4 py-2 text-sm font-medium text-slate-900">{li.description}</td>
                  <td className="px-4 py-2 text-xs text-slate-500 font-mono">{li.hsn}</td>
                  <td className="px-4 py-2 text-sm tabular-nums">{li.qty}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">{li.uom}</td>
                  <td className="px-4 py-2 text-xs text-slate-500 tabular-nums">{fmtINR(li.unit_price)}</td>
                  <td className="px-4 py-2 text-xs text-slate-700 tabular-nums">{fmtINR(li.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-6 text-sm">
            <div><span className="text-slate-500">Subtotal:</span> <span className="font-medium tabular-nums">{fmtINR(result.subtotal)}</span></div>
            <div><span className="text-slate-500">GST:</span> <span className="font-medium tabular-nums">{fmtINR(result.gst_amount)}</span></div>
            <div className="text-base"><span className="text-slate-500">Total:</span> <span className="font-bold tabular-nums">{fmtINR(result.total)}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</div>
      <div className="text-sm font-medium text-slate-900 mt-0.5 truncate" title={value}>{value || "—"}</div>
    </div>
  );
}
